import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Activity as ActivityIcon, ArrowRight, Gauge } from "lucide-react";
import { Navbar } from "@/components/landing/Navbar";
import { getRecentPireps } from "@/lib/api";

const scoreColor = (score) => {
  const s = Number(score);
  if (!Number.isFinite(s)) return "text-[#A1A1AA]";
  if (s >= 90) return "text-emerald-400";
  if (s >= 70) return "text-[#D4AF37]";
  return "text-rose-400";
};

const FlightCard = ({ f, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.35, delay: index * 0.03 }}
    data-testid={`activity-card-${index}`}
    className="flex items-center justify-between gap-4 rounded-2xl border border-white/5 bg-[#12141D] p-5 transition-colors duration-300 hover:border-[#D4AF37]/30"
  >
    <div className="min-w-0">
      <div className="flex items-center gap-2">
        <span className="font-mono-data text-sm font-bold text-[#D4AF37]">{f.flight_number}</span>
        <span className="text-xs text-[#A1A1AA]">{f.aircraft_reg || f.aircraft || "—"}</span>
      </div>
      <div className="mt-1 flex items-center gap-2 text-sm text-[#FDFBF7]">
        <span>{f.dep_icao || f.dep_iata || "—"}</span>
        <ArrowRight className="h-3.5 w-3.5 text-[#A1A1AA]" />
        <span>{f.arr_icao || f.arr_iata || "—"}</span>
      </div>
      <p className="mt-1 text-xs text-[#A1A1AA]">
        {f.pilot_name} <span className="font-mono-data">({f.pilot_ident})</span>
      </p>
    </div>
    <div className="flex-shrink-0 text-right">
      <p className={`flex items-center justify-end gap-1 font-mono-data text-sm font-bold ${scoreColor(f.pirep_score)}`}>
        <Gauge className="h-3.5 w-3.5" /> {f.pirep_score ?? "—"}
      </p>
      <p className="text-xs text-[#A1A1AA]">{f.pirep_time ? `${f.pirep_time} min` : "—"}</p>
    </div>
  </motion.div>
);

export default function Activity() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getRecentPireps()
      .then((r) => setItems(r?.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0B10]" data-testid="activity-page">
      <Navbar />
      <main className="mx-auto max-w-3xl px-5 pb-24 pt-28 lg:px-10 lg:pt-32">
        <div className="max-w-2xl">
          <div className="mb-4 h-0.5 w-16 bg-[#8A1538]" />
          <p className="text-overline flex items-center gap-2 text-[#8A1538]">
            <ActivityIcon className="h-4 w-4" /> Live Activity
          </p>
          <h1 className="mt-3 font-display text-4xl font-black tracking-tight text-[#FDFBF7] lg:text-5xl [text-wrap:balance]">
            Recently completed flights
          </h1>
        </div>

        <div className="mt-10 space-y-3">
          {loading ? (
            <p className="py-16 text-center text-sm text-[#A1A1AA]">Loading activity…</p>
          ) : items.length === 0 ? (
            <p className="py-16 text-center text-sm text-[#A1A1AA]" data-testid="activity-empty">
              No recent flights to show.
            </p>
          ) : (
            items.map((f, i) => <FlightCard key={`${f.pilot_id}-${f.flight_number}-${i}`} f={f} index={i} />)
          )}
        </div>
      </main>
    </div>
  );
}
